import { z, type ZodType } from 'zod';

import { generateClientRequestId } from '@/lib/offline/clientRequestId';

import type { OfflineQueuedResult } from '@/lib/offline/types';
import type { SelectedModifierGroup } from '@/lib/ordering/calculateItemTotal';
import { coerceOrderStatus } from '@/lib/orders/status';
import { ORDER_STATUS_SEQUENCE } from '@/types/orderStatus';
import type { PaymentRecord } from './payments';
import { ApiResponseError, assertOk, parseJson } from './response';



const normaliseQueueId = (value: unknown): string => {

  if (typeof value === 'string' && value.length > 0) {

    return value;

  }

  return `queued-${Date.now().toString(36)}`;

};



type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type JsonRequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch as ApiFetchFn;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

async function requestJson<T>(
  path: string,
  schema: z.ZodType<T>,
  { method = 'GET', body, signal, headers = {} }: JsonRequestOptions = {},
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };

  if (body !== undefined && !('Content-Type' in headers)) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    headers: requestHeaders,
  });

  if (response.status === 204) {
    await assertOk(response);
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, schema);
}

const SelectedModifierGroupSchema: ZodType<SelectedModifierGroup> = z.any();

const RawOrderTotalsSchema = z
  .object({
    currency: z.string().optional(),
    subtotal_cents: z.number().optional(),
    tax_cents: z.number().optional(),
    service_cents: z.number().optional(),
    surcharge_cents: z.number().optional(),
    tip_cents: z.number().optional(),
    total_cents: z.number().optional(),
  })
  .passthrough();
type RawOrderTotals = z.infer<typeof RawOrderTotalsSchema>;

const RawOrderItemPricingSchema = z
  .object({
    basePriceCents: z.number().nullable().optional(),
    modifiersTotalCents: z.number().nullable().optional(),
    unitPriceCents: z.number().nullable().optional(),
  })
  .passthrough();

const RawOrderItemSchema = z
  .object({
    id: z.string().optional(),
    name: z.string(),
    quantity: z.number(),
    priceCents: z.number().optional(),
    totalCents: z.number().optional(),
    notes: z.string().nullable().optional(),
    modifiers: z.array(SelectedModifierGroupSchema).optional(),
    pricing: RawOrderItemPricingSchema.nullable().optional(),
    allergens: z.array(z.string()).optional(),
    menuItemId: z.string().nullable().optional(),
    remainingQuantity: z.number().optional(),
    readyQuantity: z.number().optional(),
    status: z.string().optional(),
    stationId: z.string().nullable().optional(),
    routeStrategy: z.string().nullable().optional(),
    prepSecondsTarget: z.number().nullable().optional(),
    timerStartedAt: z.string().nullable().optional(),
    timerPausedAt: z.string().nullable().optional(),
    timerTotalPauseMs: z.number().optional(),
    readyAt: z.string().nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

const RawOrderPaymentSchema = z
  .object({
    id: z.string(),
    method: z.string(),
    status: z.string(),
    direction: z.string(),
    amountCents: z.number(),
    capturedAt: z.string().nullable(),
  })
  .passthrough();

const RawOrderDiscountStateSchema = z
  .object({
    id: z.string().optional(),
    stepOrder: z.number().optional(),
    approverRole: z.string().nullable().optional(),
    approverId: z.string().nullable().optional(),
    status: z.string().optional(),
    respondedAt: z.union([z.string(), z.number()]).nullable().optional(),
    responseNote: z.string().nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();
type RawOrderDiscountState = z.infer<typeof RawOrderDiscountStateSchema>;

const RawOrderDiscountSchema = z
  .object({
    amountCents: z.number().optional(),
    percentBps: z.number().nullable().optional(),
    policyCode: z.string().nullable().optional(),
    policyName: z.string().nullable().optional(),
    reasonCode: z.string().nullable().optional(),
    reasonNote: z.string().nullable().optional(),
    requiresApproval: z.boolean().optional(),
    approvalStatus: z.string().optional(),
    approvalRequestId: z.string().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.union([z.string(), z.number()]).nullable().optional(),
    workflowState: z.array(RawOrderDiscountStateSchema).optional(),
    policySnapshot: z.record(z.unknown()).nullable().optional(),
    financialProfileSnapshot: z.record(z.unknown()).nullable().optional(),
    notifiedAt: z.union([z.string(), z.number()]).nullable().optional(),
    escalatedAt: z.union([z.string(), z.number()]).nullable().optional(),
  })
  .passthrough();
type RawOrderDiscount = z.infer<typeof RawOrderDiscountSchema>;

const RawOrderSchema = z
  .object({
    id: z.string(),
    code: z.string().optional(),
    table: z.string().nullable().optional(),
    tableId: z.string().nullable().optional(),
    tableNumber: z.number().nullable().optional(),
    status: z.string(),
    paymentStatus: z.string().optional(),
    type: z.string().optional(),
    source: z.string().optional(),
    placedAt: z.string().optional(),
    updatedAt: z.string().optional(),
    customerName: z.string().nullable().optional(),
    customerPhone: z.string().nullable().optional(),
    totals: RawOrderTotalsSchema.nullable().optional(),
    payment: RawOrderPaymentSchema.nullable().optional(),
    sequenceMicros: z.number().optional(),
    estimatedPrepSeconds: z.number().nullable().optional(),
    estimatedReadyAt: z.string().nullable().optional(),
    etaSeconds: z.number().nullable().optional(),
    stationSnapshot: z.unknown().optional(),
    items: z.array(RawOrderItemSchema).optional(),
    discount: RawOrderDiscountSchema.nullable().optional(),
    collectedCents: z.number().optional(),
    refundedCents: z.number().optional(),
    netPaidCents: z.number().optional(),
    outstandingBalanceCents: z.number().optional(),
    refundDueCents: z.number().optional(),
  })
  .passthrough();
type RawOrder = z.infer<typeof RawOrderSchema>;

const RawOrdersPayloadSchema = z
  .object({
    orders: z.array(RawOrderSchema).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
    total: z.number().optional(),
    totalPages: z.number().optional(),
    hasNextPage: z.boolean().optional(),
    metrics: z
      .object({
        totalOrdersToday: z.number().optional(),
        revenueToday: z.number().optional(),
        activeOrders: z.number().optional(),
        avgOrder: z.number().optional(),
      })
      .nullable()
      .optional(),
    statusCounts: z
      .array(
        z
          .object({
            status: z.string().optional(),
            count: z.number().optional(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();

const RawFetchOrdersResponseSchema = z
  .object({
    ok: z.boolean().optional(),
    data: RawOrdersPayloadSchema.optional(),
  })
  .passthrough();

const UpdateOrderStatusResponseSchema = z
  .object({
    order: RawOrderSchema,
  })
  .passthrough();

const UpdateOrderEnvelopeSchema = z
  .object({
    order: RawOrderSchema.nullable(),
    totals: RawOrderTotalsSchema.nullable().optional(),
    outstandingBalanceCents: z.number().optional(),
    refundDueCents: z.number().optional(),
  })
  .passthrough();

const PaymentRecordSchema: ZodType<PaymentRecord> = z
  .object({
    id: z.string(),
    orderId: z.string(),
    amountCents: z.number(),
    tipCents: z.number().nullable().optional(),
    method: z.string(),
    status: z.string(),
    direction: z.string().nullable().optional(),
    currency: z.string().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
    orderCode: z.string().nullable().optional(),
    createdAt: z.string(),
    capturedAt: z.string().nullable().optional(),
    refundedAt: z.string().nullable().optional(),
    refundReason: z.string().nullable().optional(),
    processedBy: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    customerRating: z.number().nullable().optional(),
  })
  .passthrough();

const PaymentIntentSchema = z
  .object({
    id: z.string(),
    status: z.string(),
  })
  .passthrough();

const PayOrderCashResponseSchema = z
  .object({
    payment: PaymentRecordSchema,
  })
  .passthrough();

const PayOrderCardResponseSchema = z
  .object({
    payment: PaymentRecordSchema,
    intent: PaymentIntentSchema.optional(),
  })
  .passthrough();

const RefundOrderResponseSchema = z
  .object({
    payment: PaymentRecordSchema,
  })
  .passthrough();

const QueuedOrderResponseSchema = z
  .object({
    queued: z.literal(true),
    queueId: z.string().optional(),
    meta: z.unknown().optional(),
  })
  .passthrough();

const CreateOrderResponseSchema = z.union([
  z
    .object({
      order: RawOrderSchema,
    })
    .passthrough(),
  QueuedOrderResponseSchema,
]);

const RawFetchKitchenOrdersResponseSchema = z.union([
  z
    .object({
      ok: z.boolean().optional(),
      data: z.unknown().optional(),
      orders: z.unknown().optional(),
    })
    .passthrough(),
  z.null(),
  z.undefined(),
]);

const UpdateKitchenOrderStatusSchema = z
  .object({
    order: z.unknown().optional(),
    id: z.string().optional(),
    status: z.string().optional(),
    ok: z.boolean().optional(),
  })
  .passthrough();

const UpdateKitchenOrderPriorityResponseSchema = z
  .object({
    priority: z.enum(['NORMAL', 'HIGH', 'URGENT']),
    prepSeconds: z.number().nullable(),
    estimatedReadyAt: z.string().nullable(),
  })
  .passthrough();

const UpdateKitchenOrderPauseResponseSchema = z
  .object({
    isPaused: z.boolean(),
    pausedAt: z.string().nullable(),
    pauseReason: z.string().nullable().optional(),
    lastResumeAt: z.string().nullable().optional(),
  })
  .passthrough();

const UpdateKitchenOrderItemResponseSchema = z
  .object({
    ok: z.boolean(),
    item: z
      .object({
        id: z.string(),
        readyQuantity: z.number(),
        remainingQuantity: z.number(),
        status: z.string(),
        readyAt: z.string().nullable(),
      })
      .passthrough(),
    order: z
      .object({
        id: z.string(),
        kdsReadyQuantityTotal: z.number(),
        kdsPartialReady: z.boolean(),
      })
      .passthrough(),
  })
  .passthrough();

const NotifyKitchenOrderResponseSchema = z
  .object({
    notifiedAt: z.string(),
  })
  .passthrough();

const RecentOrderSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    status: z.string().optional(),
    total_cents: z.number(),
    created_at: z.string(),
    table_id: z.string().nullable(),
  })
  .passthrough();

const RecentOrdersResponseSchema = z
  .object({
    orders: z.array(RecentOrderSchema),
  })
  .passthrough();

const BasicOkResponseSchema = z
  .object({
    ok: z.boolean(),
  })
  .passthrough();

const normaliseTotals = (totals?: RawOrderTotals | null) => {

  if (!totals) {

    return {

      currency: 'USD',

      subtotalCents: 0,

      taxCents: 0,

      serviceCents: 0,

      surchargeCents: 0,

      tipCents: 0,

      totalCents: 0,

    };

  }



  return {

    currency: typeof totals.currency === 'string' && totals.currency.length > 0 ? totals.currency : 'USD',

    subtotalCents: Number(totals.subtotal_cents ?? 0) || 0,

    taxCents: Number(totals.tax_cents ?? 0) || 0,

    serviceCents: Number(totals.service_cents ?? 0) || 0,

    surchargeCents: Number(totals.surcharge_cents ?? 0) || 0,

    tipCents: Number(totals.tip_cents ?? 0) || 0,

    totalCents: Number(totals.total_cents ?? 0) || 0,

  };

};

const toIsoOrNull = (value: unknown): string | null => {

  if (typeof value === 'number' && Number.isFinite(value)) {

    return new Date(value).toISOString();

  }

  if (typeof value === 'string') {

    const parsed = Date.parse(value);

    if (!Number.isNaN(parsed)) {

      return new Date(parsed).toISOString();

    }

  }

  return null;

};

const normalizeDiscountState = (state: RawOrderDiscountState | undefined, index: number): OrderDiscountState | null => {

  if (!state) {

    return null;

  }

  const stepOrder =

    typeof state.stepOrder === 'number' && Number.isFinite(state.stepOrder)

      ? Math.trunc(state.stepOrder)

      : index;

  const id =

    typeof state.id === 'string' && state.id.length > 0 ? state.id : `step-${stepOrder}`;

  const approverRole =

    typeof state.approverRole === 'string' && state.approverRole.length > 0

      ? state.approverRole

      : null;

  const approverId =

    typeof state.approverId === 'string' && state.approverId.length > 0

      ? state.approverId

      : null;

  const status =

    typeof state.status === 'string' && state.status.length > 0 ? state.status : 'pending';

  const respondedAt = toIsoOrNull(state.respondedAt ?? null);

  const responseNote =

    typeof state.responseNote === 'string' && state.responseNote.length > 0

      ? state.responseNote

      : null;

  const metadata =

    state.metadata && typeof state.metadata === 'object' && !Array.isArray(state.metadata)

      ? (state.metadata as Record<string, unknown>)

      : {};

  return {

    id,

    stepOrder,

    approverRole,

    approverId,

    status,

    respondedAt,

    responseNote,

    metadata,

  };

};

const toRecord = (value: unknown): Record<string, unknown> => {

  if (!value || typeof value !== 'object' || Array.isArray(value)) {

    return {};

  }

  return value as Record<string, unknown>;

};

const normalizeDiscount = (discount: RawOrderDiscount | null | undefined): OrderDiscountSummary | null => {

  if (!discount) {

    return null;

  }

  const amountCents =

    typeof discount.amountCents === 'number' && Number.isFinite(discount.amountCents)

      ? Math.round(discount.amountCents)

      : 0;

  const percentBps =

    typeof discount.percentBps === 'number' && Number.isFinite(discount.percentBps)

      ? Math.round(discount.percentBps)

      : null;

  const requiresApproval = Boolean(discount.requiresApproval);

  const workflowState = Array.isArray(discount.workflowState)

    ? discount.workflowState

        .map((entry, index) => normalizeDiscountState(entry, index))

        .filter((entry): entry is OrderDiscountState => entry !== null)

    : [];

  const policySnapshot = toRecord(discount.policySnapshot ?? {});

  const financialProfileSnapshot = toRecord(discount.financialProfileSnapshot ?? {});

  const policyName =

    typeof discount.policyName === 'string' && discount.policyName.length > 0

      ? discount.policyName

      : typeof (policySnapshot as { name?: unknown }).name === 'string'

        ? ((policySnapshot as { name?: string }).name as string)

        : null;

  return {

    amountCents,

    percentBps,

    policyCode: typeof discount.policyCode === 'string' ? discount.policyCode : null,

    policyName,

    reasonCode: typeof discount.reasonCode === 'string' ? discount.reasonCode : null,

    reasonNote: typeof discount.reasonNote === 'string' ? discount.reasonNote : null,

    requiresApproval,

    approvalStatus:

      typeof discount.approvalStatus === 'string' && discount.approvalStatus.length > 0

        ? discount.approvalStatus

        : requiresApproval

          ? 'pending'

          : 'approved',

    approvalRequestId:

      typeof discount.approvalRequestId === 'string' && discount.approvalRequestId.length > 0

        ? discount.approvalRequestId

        : null,

    approvedBy:

      typeof discount.approvedBy === 'string' && discount.approvedBy.length > 0

        ? discount.approvedBy

        : null,

    approvedAt: toIsoOrNull(discount.approvedAt ?? null),

    workflowState,

    policySnapshot,

    financialProfileSnapshot,

    notifiedAt: toIsoOrNull(discount.notifiedAt ?? null),

    escalatedAt: toIsoOrNull(discount.escalatedAt ?? null),

  };

};



const mapRawOrder = (raw: RawOrder) => {

  const totals = normaliseTotals(raw.totals ?? null);
  const collectedCents =
    typeof (raw as { collectedCents?: unknown }).collectedCents === 'number' &&
    Number.isFinite((raw as { collectedCents?: number }).collectedCents)
      ? Math.max(0, Math.round((raw as { collectedCents: number }).collectedCents))
      : 0;
  const refundedCents =
    typeof (raw as { refundedCents?: unknown }).refundedCents === 'number' &&
    Number.isFinite((raw as { refundedCents?: number }).refundedCents)
      ? Math.max(0, Math.round((raw as { refundedCents: number }).refundedCents))
      : 0;
  const netPaidCents =
    typeof (raw as { netPaidCents?: unknown }).netPaidCents === 'number' &&
    Number.isFinite((raw as { netPaidCents?: number }).netPaidCents)
      ? Math.max(0, Math.round((raw as { netPaidCents: number }).netPaidCents))
      : Math.max(0, collectedCents - refundedCents);
  const outstandingBalanceCents =
    typeof (raw as { outstandingBalanceCents?: unknown }).outstandingBalanceCents === 'number' &&
    Number.isFinite((raw as { outstandingBalanceCents?: number }).outstandingBalanceCents)
      ? Math.max(0, Math.round((raw as { outstandingBalanceCents: number }).outstandingBalanceCents))
      : Math.max(0, totals.totalCents - netPaidCents);
  const refundDueCents =
    typeof (raw as { refundDueCents?: unknown }).refundDueCents === 'number' &&
    Number.isFinite((raw as { refundDueCents?: number }).refundDueCents)
      ? Math.max(0, Math.round((raw as { refundDueCents: number }).refundDueCents))
      : Math.max(0, netPaidCents - totals.totalCents);

  const items = Array.isArray(raw.items)
    ? raw.items.map((item) => {
        const modifiers = Array.isArray(item.modifiers)
          ? (item.modifiers as SelectedModifierGroup[])
          : [];
        const pricing = item.pricing
          ? {
              basePriceCents: Number(item.pricing.basePriceCents ?? 0) || 0,
              modifiersTotalCents: Number(item.pricing.modifiersTotalCents ?? 0) || 0,
              unitPriceCents: Number(item.pricing.unitPriceCents ?? 0) || 0,
            }
          : null;
        const allergens = Array.isArray(item.allergens)
          ? (item.allergens as unknown[]).filter(
              (tag): tag is string => typeof tag === 'string' && tag.trim().length > 0,
            )
          : [];

        return {
          id: item.id,
          menuItemId: item.menuItemId ?? null,
          name: item.name,
          quantity: item.quantity,
          remainingQuantity: item.remainingQuantity,
          readyQuantity: item.readyQuantity,
          status: item.status,
          stationId: item.stationId ?? null,
          routeStrategy: item.routeStrategy ?? null,
          prepSecondsTarget: item.prepSecondsTarget ?? null,
          timerStartedAt: item.timerStartedAt ?? null,
          timerPausedAt: item.timerPausedAt ?? null,
          timerTotalPauseMs: item.timerTotalPauseMs,
          readyAt: item.readyAt ?? null,
          priceCents: item.priceCents,
          totalCents: item.totalCents,
          notes: item.notes ?? null,
          modifiers,
          pricing,
          allergens,
          metadata: item.metadata ?? undefined,
        };
      })
    : undefined;

  const payment = raw.payment
    ? {
        id: raw.payment.id,
        method: raw.payment.method,
        status: raw.payment.status,
        direction: raw.payment.direction,
        amountCents: raw.payment.amountCents,
        capturedAt: raw.payment.capturedAt ?? null,
      }
    : null;

  const stationSnapshot =
    raw.stationSnapshot && typeof raw.stationSnapshot === 'object'
      ? (raw.stationSnapshot as StationSnapshot)
      : null;
  const estimatedReadyAtIso =
    typeof raw.estimatedReadyAt === 'string' && raw.estimatedReadyAt.length > 0
      ? raw.estimatedReadyAt
      : undefined;
  const etaSeconds =
    typeof raw.etaSeconds === 'number' && Number.isFinite(raw.etaSeconds)
      ? Math.max(0, Math.round(raw.etaSeconds))
      : null;

  return {
    id: raw.id,
    code: raw.code,
    table: raw.table ?? null,
    tableId: raw.tableId ?? null,
    tableNumber: raw.tableNumber ?? null,
    status: coerceOrderStatus(raw.status),
    paymentStatus: raw.paymentStatus ?? undefined,
    type: raw.type,
    source: raw.source,
    totalCents: totals.totalCents,
    subtotalCents: totals.subtotalCents,
    taxCents: totals.taxCents,
    serviceCents: totals.serviceCents,
    surchargeCents: totals.surchargeCents,
    tipCents: totals.tipCents,
    currency: totals.currency,
    collectedCents,
    refundedCents,
    netPaidCents,
    outstandingBalanceCents,
    refundDueCents,
    placedAt: raw.placedAt,
    updatedAt: raw.updatedAt,
    createdAt: raw.placedAt,
    items,
    customerName: raw.customerName ?? null,
    customerPhone: raw.customerPhone ?? null,
    paymentMethod: payment?.method ?? undefined,
    totals,
    payment,
    sequenceMicros: typeof raw.sequenceMicros === 'number' ? raw.sequenceMicros : undefined,
    estimatedPrepSeconds:
      typeof raw.estimatedPrepSeconds === 'number' ? raw.estimatedPrepSeconds : undefined,
    estimatedReadyAt: estimatedReadyAtIso ?? null,
    etaSeconds,
    stationSnapshot: stationSnapshot as StationSnapshot | null,
    throttleOverrideApplied:
      typeof stationSnapshot === 'object' && stationSnapshot !== null
        ? Boolean((stationSnapshot as { overrideApplied?: boolean }).overrideApplied)
        : false,
    discount: normalizeDiscount(raw.discount ?? null),
    loyaltyPointsAwarded:
      typeof raw.loyaltyPointsAwarded === 'number' && Number.isFinite(raw.loyaltyPointsAwarded)
        ? Math.max(0, Math.round(raw.loyaltyPointsAwarded))
        : 0,
    loyaltyPointsRedeemed:
      typeof raw.loyaltyPointsRedeemed === 'number' && Number.isFinite(raw.loyaltyPointsRedeemed)
        ? Math.max(0, Math.round(raw.loyaltyPointsRedeemed))
        : 0,
    loyaltyProgramSnapshot:
      raw.loyaltyProgramSnapshot && typeof raw.loyaltyProgramSnapshot === 'object'
        ? (raw.loyaltyProgramSnapshot as Record<string, unknown>)
        : null,
  };
};

export type OrderSummary = ReturnType<typeof mapRawOrder>;



export async function fetchOrders(params: FetchOrdersParams = {}, signal?: AbortSignal): Promise<FetchOrdersResponse> {

  const query = new URLSearchParams();



  if (params.id) {
    query.set('id', params.id);
  }

  if (params.status && params.status !== 'ALL') {

    query.set('status', params.status);

  }



  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {

    query.set('limit', Math.max(1, Math.trunc(params.limit)).toString());

  }



  if (params.sort) {

    query.set('sort', params.sort);

  }



  if (params.search) {

    query.set('search', params.search);

  }



  if (typeof params.page === 'number' && Number.isFinite(params.page) && params.page > 0) {

    query.set('page', Math.trunc(params.page).toString());

  }



  if (typeof params.pageSize === 'number' && Number.isFinite(params.pageSize) && params.pageSize > 0) {

    query.set('pageSize', Math.trunc(params.pageSize).toString());

  }



  const path = `/orders${query.toString() ? `?${query.toString()}` : ''}`;

  const response = await requestJson(path, RawFetchOrdersResponseSchema, { signal });

  if (!response || response.ok === false) {

    throw new Error('Failed to load orders');

  }

  const data = response.data ?? {};

  const orders = Array.isArray(data.orders) ? data.orders.map(mapRawOrder) : [];

  const resolvedPageSize =
    typeof data.pageSize === 'number' && Number.isFinite(data.pageSize) && data.pageSize > 0
      ? Math.trunc(data.pageSize)
      : ((params.pageSize ?? params.limit ?? orders.length) || 50);

  const resolvedTotal =
    typeof data.total === 'number' && Number.isFinite(data.total)
      ? Math.max(0, Math.trunc(data.total))
      : orders.length;

  const resolvedTotalPages =
    typeof data.totalPages === 'number' && Number.isFinite(data.totalPages) && data.totalPages > 0
      ? Math.trunc(data.totalPages)
      : Math.max(1, Math.ceil(resolvedTotal / Math.max(resolvedPageSize, 1)));

  const resolvedPage =
    typeof data.page === 'number' && Number.isFinite(data.page) && data.page > 0
      ? Math.min(Math.trunc(data.page), resolvedTotalPages)
      : 1;

  const metricsRaw = data.metrics ?? {};

  const metrics = {
    totalOrdersToday: Number(metricsRaw.totalOrdersToday ?? 0) || 0,
    revenueToday: Number(metricsRaw.revenueToday ?? 0) || 0,
    activeOrders: Number(metricsRaw.activeOrders ?? 0) || 0,
    avgOrder: Number(metricsRaw.avgOrder ?? 0) || 0,
  };

  const statusCounts = Array.isArray(data.statusCounts)
    ? (() => {
        const bucket = new Map<OrderStatus, number>();
        for (const entry of data.statusCounts) {
          const status = coerceOrderStatus(entry?.status);
          const count = Number((entry && (entry as { count?: number }).count) ?? 0) || 0;
          bucket.set(status, (bucket.get(status) ?? 0) + count);
        }
        return ORDER_STATUS_SEQUENCE.map((status) => ({ status, count: bucket.get(status) ?? 0 })).filter((entry) => entry.count > 0);
      })()
    : [];

  return {
    orders,
    page: resolvedPage,
    pageSize: resolvedPageSize,
    total: resolvedTotal,
    totalPages: resolvedTotalPages,
    hasNextPage: typeof data.hasNextPage === 'boolean' ? data.hasNextPage : resolvedPage < resolvedTotalPages,
    metrics,
    statusCounts,
  };

}



export interface UpdateOrderStatusPayload {

  status: OrderStatus;

}



export async function updateOrderStatus(
  orderId: string,
  payload: UpdateOrderStatusPayload,
): Promise<{ order: OrderSummary }> {
  const response = await requestJson(
    `/orders/${orderId}/status`,
    UpdateOrderStatusResponseSchema,
    {
      method: 'PUT',
      body: payload,
    },
  );

  return { order: mapRawOrder(response.order) };
}



export interface CreateOrderItemPayload {

  itemId: string;

  quantity: number;

  notes?: string;

  modifiers?: SelectedModifierGroup[];

  pricing?: OrderItemPricingSnapshot;

}



export interface CreateOrderPayload {

  type: 'dine-in' | 'takeout' | 'delivery';

  tableId?: string;

  customerName?: string;

  customerPhone?: string;

  items: CreateOrderItemPayload[];

  payment?: {

    method: 'card' | 'cash';

    tipCents?: number;

  };

  notes?: string;

  clientRequestId?: string;

  loyalty?: {

    optIn?: boolean;

  };

  loyaltyOptIn?: boolean;
  loyaltyRedeemPoints?: number;

}



export type CreateOrderResult = { order: OrderSummary } | OfflineQueuedResult;



export interface UpdateOrderItemInput {
  id?: string;
  menuItemId?: string | null;
  quantity: number;
  priceCents: number;
  name?: string | null;
  notes?: string | null;
  taxRateBps?: number | null;
  modifiers?: SelectedModifierGroup[];
  pricing?: OrderItemPricingSnapshot;
}



export interface UpdateOrderPayload {
  items: UpdateOrderItemInput[];
  tableId?: string | null;
  tableNumber?: number | null;
  notes?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  tipCents?: number | null;
  reason?: string | null;
}



export interface UpdateOrderResponse {
  order: OrderSummary | null;
  totals: {
    currency: string;
    subtotalCents: number;
    taxCents: number;
    serviceCents: number;
    surchargeCents: number;
    tipCents: number;
    totalCents: number;
  };
  outstandingBalanceCents: number;
  refundDueCents: number;
}



export async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResult> {
  const clientRequestId = payload.clientRequestId ?? generateClientRequestId('pos');
  const requestPayload = { ...payload, clientRequestId };

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/orders', {
    method: 'POST',
    body: JSON.stringify(requestPayload),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  if (response.status === 204) {
    return {
      queued: true,
      queueId: normaliseQueueId(null),
    };
  }

  await assertOk(response);

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiResponseError('Expected JSON response', response.status);
  }

  const parsed = await parseJson(response, CreateOrderResponseSchema);

  if ('queued' in parsed && parsed.queued === true) {
    return {
      queued: true,
      queueId: normaliseQueueId(parsed.queueId),
      meta: parsed.meta,
    };
  }

  return {
    order: mapRawOrder(parsed.order),
  };
}



export async function updateOrder(orderId: string, payload: UpdateOrderPayload): Promise<UpdateOrderResponse> {
  const response = await requestJson(
    `/orders/${orderId}`,
    UpdateOrderEnvelopeSchema,
    {
      method: 'PUT',
      body: payload,
    },
  );

  const order = response.order ? mapRawOrder(response.order) : null;
  const totals = normaliseTotals(response.totals ?? null);
  const outstanding =
    typeof response.outstandingBalanceCents === 'number' && Number.isFinite(response.outstandingBalanceCents)
      ? Math.max(0, Math.round(response.outstandingBalanceCents))
      : 0;
  const refund =
    typeof response.refundDueCents === 'number' && Number.isFinite(response.refundDueCents)
      ? Math.max(0, Math.round(response.refundDueCents))
      : 0;

  return {
    order,
    totals,
    outstandingBalanceCents: outstanding,
    refundDueCents: refund,
  };
}



export interface PayOrderCashPayload {

  amount: number;

  tip?: number;

}



export async function payOrderCash(orderId: string, payload: PayOrderCashPayload): Promise<{ payment: PaymentRecord }> {
  return requestJson(
    `/orders/${orderId}/pay/cash`,
    PayOrderCashResponseSchema,
    {
      method: 'POST',
      body: payload,
    },
  );
}



export interface PayOrderCardPayload {

  amount?: number;

  tip?: number;

}



export async function payOrderTestCard(
  orderId: string,
  payload: PayOrderCardPayload = {},
): Promise<{ payment: PaymentRecord; intent?: { id: string; status: string } }> {
  return requestJson(
    `/orders/${orderId}/pay/card`,
    PayOrderCardResponseSchema,
    {
      method: 'POST',
      body: payload,
    },
  );
}

export interface RefundOrderPayload {
  amount: number;
  reason?: string;
  method?: string;
  referenceId?: string;
  processor?: string;
  metadata?: Record<string, unknown>;
}

export async function refundOrder(
  orderId: string,
  payload: RefundOrderPayload,
): Promise<{ payment: PaymentRecord }> {
  return requestJson(
    `/orders/${orderId}/refund`,
    RefundOrderResponseSchema,
    {
      method: 'POST',
      body: payload,
    },
  );
}



export interface SubscribeRealtimeOptions {

  onInserted?: (order: OrderSummary) => void;

  onUpdated?: (order: OrderSummary) => void;

}



export type RealtimeUnsubscribe = () => void;



export interface RealtimeChannelLike {

  unsubscribe: () => void;

}



export type RealtimeFactory = () => RealtimeChannelLike | null;



export function createOrdersRealtimeSubscription(factory: RealtimeFactory, { onInserted, onUpdated }: SubscribeRealtimeOptions = {}): RealtimeUnsubscribe {

  const channel = factory();



  if (!channel) {

    return () => {};

  }



  const safeOnInserted = onInserted ?? (() => {});

  const safeOnUpdated = onUpdated ?? (() => {});



  // Consumers must wire event handlers on the created channel

  // This helper simply normalises the teardown for callers.

  return () => {

    try {

      void safeOnInserted;

      void safeOnUpdated;

      channel.unsubscribe();

    } catch (error) {

      console.warn('[ordersRealtime] unsubscribe failed', error);

    }

  };

}



export type KitchenOrderItemStatus =
  | 'QUEUED'
  | 'FIRED'
  | 'PARTIAL_READY'
  | 'READY'
  | 'RECALLED'
  | 'CANCELLED';

export interface KitchenOrderItem {
  id?: string;
  menuItemId?: string | null;
  name: string;
  quantity: number;
  remainingQuantity?: number;
  readyQuantity?: number;
  status?: KitchenOrderItemStatus;
  stationId?: string | null;
  routeStrategy?: string | null;
  prepSecondsTarget?: number | null;
  timerStartedAt?: string | null;
  timerPausedAt?: string | null;
  timerTotalPauseMs?: number;
  readyAt?: string | null;
  priceCents?: number;
  totalCents?: number;
  notes?: string | null;
  modifiers?: SelectedModifierGroup[];
  pricing?: OrderItemPricingSnapshot | null;
  allergens?: string[];
  metadata?: Record<string, unknown>;
}

export type KitchenOrderPriority = 'NORMAL' | 'HIGH' | 'URGENT';

export interface KitchenOrderTotals {
  currency: string;
  subtotal_cents: number;
  tax_cents: number;
  service_cents: number;
  surcharge_cents: number;
  tip_cents: number;
  total_cents: number;
}

export interface KitchenOrderPayment {
  id: string;
  method: string;
  status: string;
  direction: string;
  amountCents: number;
  capturedAt: string;
}

export interface KitchenOrderKdsState {
  priority: KitchenOrderPriority;
  prepSeconds: number | null;
  estimatedReadyAt: string | null;
  elapsedSeconds: number;
  isPaused: boolean;
  pausedAt: string | null;
  pauseReason: string | null;
  lastResumeAt: string | null;
  waiterNotifiedAt: string | null;
  partialReady: boolean;
  readyQuantityTotal: number;
  handheldOrigin: string | null;
}

export interface KitchenOrder {
  id: string;
  code?: string;
  table?: string | null;
  tableId?: string | null;
  tableNumber?: number | null;
  customerName?: string | null;
  status: OrderStatus;
  paymentStatus: string;
  type: string;
  totals: KitchenOrderTotals;
  placedAt?: string;
  updatedAt?: string;
  locationId?: string | null;
  payment: KitchenOrderPayment | null;
  kds: KitchenOrderKdsState;
  items: KitchenOrderItem[];
  sequenceMicros?: number;
  estimatedPrepSeconds?: number | null;
  estimatedReadyAt?: string | null;
  etaSeconds?: number | null;
  stationSnapshot?: StationSnapshot | null;
  throttleOverrideApplied?: boolean;
}
export interface FetchKitchenOrdersParams {

  status?: OrderStatus | 'ALL';

}



export interface FetchKitchenOrdersResponse {
  orders: KitchenOrder[];
}

type RawFetchKitchenOrdersResponse = z.infer<typeof RawFetchKitchenOrdersResponseSchema>;

const normaliseKitchenOrderItem = (value: unknown): KitchenOrderItem => {
  if (!value || typeof value !== 'object') {
    return {
      name: 'Item',
      quantity: 0,
      status: 'QUEUED',
    };
  }

  const raw = value as Record<string, unknown>;
  const status = typeof raw.status === 'string' ? (raw.status.toUpperCase() as KitchenOrderItemStatus) : 'QUEUED';

  const toNumber = (input: unknown): number | undefined =>
    typeof input === 'number' && Number.isFinite(input) ? input : undefined;

  const toStringOrNull = (input: unknown): string | null =>
    typeof input === 'string' && input.length > 0 ? input : null;

  const toString = (input: unknown, fallback: string): string =>
    typeof input === 'string' && input.length > 0 ? input : fallback;

  const timerTotalPauseMs =
    typeof raw.timerTotalPauseMs === 'number' && Number.isFinite(raw.timerTotalPauseMs)
      ? Math.max(0, raw.timerTotalPauseMs)
      : undefined;

  return {
    id: typeof raw.id === 'string' ? raw.id : undefined,
    menuItemId: toStringOrNull(raw.menuItemId),
    name: toString(raw.name, 'Item'),
    quantity: Math.max(0, toNumber(raw.quantity) ?? 0),
    remainingQuantity: toNumber(raw.remainingQuantity),
    readyQuantity: toNumber(raw.readyQuantity),
    status,
    stationId: toStringOrNull(raw.stationId),
    routeStrategy: toStringOrNull(raw.routeStrategy) ?? undefined,
    prepSecondsTarget: toNumber(raw.prepSecondsTarget) ?? null,
    timerStartedAt: toStringOrNull(raw.timerStartedAt),
    timerPausedAt: toStringOrNull(raw.timerPausedAt),
    timerTotalPauseMs,
    readyAt: toStringOrNull(raw.readyAt),
    priceCents: toNumber(raw.priceCents),
    totalCents: toNumber(raw.totalCents),
    notes: toStringOrNull(raw.notes),
    modifiers: Array.isArray(raw.modifiers)
      ? (raw.modifiers as SelectedModifierGroup[])
      : undefined,
    pricing:
      raw.pricing && typeof raw.pricing === 'object'
        ? (raw.pricing as OrderItemPricingSnapshot)
        : undefined,
    allergens: Array.isArray(raw.allergens)
      ? (raw.allergens as string[]).filter((tag) => typeof tag === 'string' && tag.length > 0)
      : undefined,
    metadata:
      raw.metadata && typeof raw.metadata === 'object'
        ? (raw.metadata as Record<string, unknown>)
        : undefined,
  };
};

const normaliseKitchenKdsState = (value: unknown): KitchenOrderKdsState => {
  if (!value || typeof value !== 'object') {
    return {
      priority: 'NORMAL',
      prepSeconds: null,
      estimatedReadyAt: null,
      elapsedSeconds: 0,
      isPaused: false,
      pausedAt: null,
      pauseReason: null,
      lastResumeAt: null,
      waiterNotifiedAt: null,
      partialReady: false,
      readyQuantityTotal: 0,
      handheldOrigin: null,
    };
  }

  const raw = value as Record<string, unknown>;
  const priorityRaw = typeof raw.priority === 'string' ? raw.priority.toUpperCase() : 'NORMAL';
  const fallbackPriority: KitchenOrderPriority =
    priorityRaw === 'HIGH' || priorityRaw === 'URGENT' ? priorityRaw : 'NORMAL';

  const toStringOrNull = (input: unknown): string | null =>
    typeof input === 'string' && input.length > 0 ? input : null;

  const toNumber = (input: unknown): number | null =>
    typeof input === 'number' && Number.isFinite(input) ? input : null;

  return {
    priority: fallbackPriority,
    prepSeconds: toNumber(raw.prepSeconds),
    estimatedReadyAt: toStringOrNull(raw.estimatedReadyAt),
    elapsedSeconds: Math.max(0, toNumber(raw.elapsedSeconds) ?? 0),
    isPaused: raw.isPaused === true,
    pausedAt: toStringOrNull(raw.pausedAt),
    pauseReason: toStringOrNull(raw.pauseReason),
    lastResumeAt: toStringOrNull(raw.lastResumeAt),
    waiterNotifiedAt: toStringOrNull(raw.waiterNotifiedAt),
    partialReady: raw.partialReady === true,
    readyQuantityTotal: Math.max(0, toNumber(raw.readyQuantityTotal) ?? 0),
    handheldOrigin: toStringOrNull(raw.handheldOrigin),
  };
};

const normaliseKitchenOrdersResponse = (payload: RawFetchKitchenOrdersResponse): KitchenOrder[] => {
  if (!payload || (typeof payload === 'object' && payload.ok === false)) {
    throw new Error('Failed to load kitchen orders');
  }

  let orders: unknown[] | undefined;

  if (payload && Array.isArray(payload.orders)) {
    orders = payload.orders as unknown[];
  } else if (payload && Array.isArray(payload.data)) {
    orders = payload.data as unknown[];
  } else if (
    payload &&
    payload.data &&
    typeof payload.data === 'object' &&
    Array.isArray((payload.data as { orders?: unknown }).orders)
  ) {
    orders = (payload.data as { orders: unknown[] }).orders;
  }

  if (!orders) {
    return [];
  }

  return orders.map((entry) => {
    const order = entry as KitchenOrder;
    const snapshot =
      order.stationSnapshot && typeof order.stationSnapshot === 'object'
        ? (order.stationSnapshot as StationSnapshot)
        : null;
    const items = Array.isArray(order.items)
      ? order.items.map((item) => normaliseKitchenOrderItem(item))
      : [];
    const kdsState = normaliseKitchenKdsState(order.kds);
    return {
      ...order,
      status: coerceOrderStatus(order.status),
      stationSnapshot: snapshot,
      throttleOverrideApplied:
        typeof order.throttleOverrideApplied === 'boolean'
          ? order.throttleOverrideApplied
          : Boolean(snapshot?.overrideApplied),
      estimatedReadyAt:
        typeof order.estimatedReadyAt === 'string' && order.estimatedReadyAt.length > 0
          ? order.estimatedReadyAt
          : null,
      etaSeconds:
        typeof order.etaSeconds === 'number' && Number.isFinite(order.etaSeconds)
          ? Math.max(0, Math.round(order.etaSeconds))
          : null,
      items,
      kds: kdsState,
    };
  });
};

const normaliseSingleKitchenOrder = (value: unknown): KitchenOrder => {
  const [order] = normaliseKitchenOrdersResponse({ orders: [value] });
  if (!order) {
    throw new Error('Failed to normalise kitchen order payload');
  }
  return order;
};

export async function fetchKitchenOrders(
  params: FetchKitchenOrdersParams = {},
  signal?: AbortSignal,
): Promise<FetchKitchenOrdersResponse> {
  const query = new URLSearchParams();

  if (params.status && params.status !== 'ALL') {
    query.set('status', params.status);
  }

  const path = `/kds/orders${query.toString() ? `?${query.toString()}` : ''}`;
  const payload = await requestJson(path, RawFetchKitchenOrdersResponseSchema, { signal });

  return {
    orders: normaliseKitchenOrdersResponse(payload),
  };
}



export interface KitchenStatusTransitionPayload {

  status: OrderStatus;

}



export async function updateKitchenOrderStatus(
  orderId: string,
  payload: KitchenStatusTransitionPayload,
): Promise<{ order: KitchenOrder | null }> {
  const response = await requestJson(
    `/kds/orders/${orderId}/status`,
    UpdateKitchenOrderStatusSchema,
    {
      method: 'PUT',
      body: payload,
    },
  );

  if (response?.order) {
    return { order: normaliseSingleKitchenOrder(response.order) };
  }

  return { order: null };
}





















export interface UpdateKitchenOrderPriorityPayload {
  priority: KitchenOrderPriority;
  prepSeconds?: number | null;
}

export interface UpdateKitchenOrderPriorityResponse {
  priority: KitchenOrderPriority;
  prepSeconds: number | null;
  estimatedReadyAt: string | null;
}

export async function updateKitchenOrderPriority(
  orderId: string,
  payload: UpdateKitchenOrderPriorityPayload,
  signal?: AbortSignal,
): Promise<UpdateKitchenOrderPriorityResponse> {
  return requestJson(
    `/kds/orders/${orderId}/priority`,
    UpdateKitchenOrderPriorityResponseSchema,
    {
      method: 'PUT',
      body: payload,
      signal,
    },
  );
}

export interface UpdateKitchenOrderPausePayload {
  isPaused: boolean;
  reason?: string | null;
}

export interface UpdateKitchenOrderPauseResponse {
  isPaused: boolean;
  pausedAt: string | null;
  pauseReason?: string | null;
  lastResumeAt?: string | null;
}

export async function updateKitchenOrderPause(
  orderId: string,
  payload: UpdateKitchenOrderPausePayload,
  signal?: AbortSignal,
): Promise<UpdateKitchenOrderPauseResponse> {
  return requestJson(
    `/kds/orders/${orderId}/pause`,
    UpdateKitchenOrderPauseResponseSchema,
    {
      method: 'PUT',
      body: payload,
      signal,
    },
  );
}

export interface UpdateKitchenOrderItemPayload {
  readyQuantity: number;
}

export interface UpdateKitchenOrderItemResponse {
  ok: boolean;
  item: {
    id: string;
    readyQuantity: number;
    remainingQuantity: number;
    status: string;
    readyAt: string | null;
  };
  order: {
    id: string;
    kdsReadyQuantityTotal: number;
    kdsPartialReady: boolean;
  };
}

export const updateKitchenOrderItemReady = (
  orderId: string,
  orderItemId: string,
  payload: UpdateKitchenOrderItemPayload,
  signal?: AbortSignal,
) =>
  requestJson(
    `/kds/orders/${orderId}/items/${orderItemId}`,
    UpdateKitchenOrderItemResponseSchema,
    {
      method: 'PUT',
      body: payload,
      signal,
    },
  );

export interface NotifyKitchenOrderResponse {
  notifiedAt: string;
}

export async function notifyKitchenOrder(orderId: string, signal?: AbortSignal): Promise<NotifyKitchenOrderResponse> {
  return requestJson(
    `/kds/orders/${orderId}/notify`,
    NotifyKitchenOrderResponseSchema,
    {
      method: 'POST',
      signal,
    },
  );
}
export interface RecentOrderSummary {
  id: string;
  code: string;
  status: string;
  total_cents: number;
  created_at: string;
  table_id: string | null;
}

export interface RecentOrdersResponse {
  orders: RecentOrderSummary[];
}

export async function getRecentOrders(
  tenantSlug: string,
  limit = 20,
  signal?: AbortSignal,
): Promise<RecentOrdersResponse> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/orders/recent?limit=${limit}`, {
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });

  await assertOk(response);
  const payload = await parseJson(response, RecentOrdersResponseSchema);

  return {
    orders: payload.orders.map((order) => ({
      id: order.id,
      code: order.code,
      status: order.status ?? 'UNKNOWN',
      total_cents: order.total_cents,
      created_at: order.created_at,
      table_id: order.table_id ?? null,
    })),
  };
}

export type OrderPrintRole = 'front_desk_receipt' | 'kitchen_ticket';

export const reprintOrder = (orderId: string, role: OrderPrintRole) =>
  requestJson(`/orders/${encodeURIComponent(orderId)}/print/${role}`, BasicOkResponseSchema, {
    method: 'POST',
  });
